/**
 * Pin Distance Calculator Component
 * AI-powered camera tool to measure ball distance from pin.
 * Uses golf ball size (1.680 inches) as reference.
 * NOTE: The ML model is MOCKED - actual implementation would require computer vision.
 */
import React, { useState, useRef, useCallback } from 'react';
import { Camera, X, Target, Loader2, Check, RotateCcw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { useOutingApi } from '../hooks/useOutingApi';
import { GOLF_BALL_DIAMETER_INCHES } from '../utils/scoringCalculations';

export default function PinDistanceCalculator({ 
  outingId, 
  teamId, 
  hole, 
  playerName,
  onSubmit, 
  onClose 
}) {
  const api = useOutingApi();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Use back camera
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraActive(true);
        setError(null);
      }
    } catch (err) {
      setError('Unable to access camera. Please allow camera permissions.');
      console.error('Camera error:', err);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  }, []);

  const captureImage = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedImage(imageData);
    stopCamera();
  }, [stopCamera]);

  const analyzeImage = async () => {
    if (!capturedImage) return;
    
    setAnalyzing(true);
    setError(null);
    
    try {
      // Call the API to analyze the image
      // NOTE: This is MOCKED on the backend
      const response = await api.calculatePinDistance(
        outingId, 
        capturedImage.split(',')[1], // Remove data URL prefix
        'user_id' // Would come from auth context
      );
      
      if (response.success) {
        setResult({
          distance_inches: response.distance_inches,
          distance_display: response.distance_display,
          confidence: response.confidence,
          ai_calculated: true
        });
      }
    } catch (err) {
      setError('Failed to analyze image. Please try again.');
      console.error('Analysis error:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSubmit = async () => {
    if (!result) return;
    
    onSubmit?.({
      team_id: teamId,
      hole,
      player_name: playerName,
      distance_inches: result.distance_inches,
      distance_display: result.distance_display,
      image_url: capturedImage,
      ai_calculated: result.ai_calculated
    });
  };

  const reset = () => {
    setCapturedImage(null);
    setResult(null);
    setError(null);
    startCamera();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 p-4 bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">Pin Distance Calculator</h2>
            <p className="text-sm text-zinc-400">Hole {hole}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              stopCamera();
              onClose?.();
            }}
            className="text-white"
          >
            <X className="w-6 h-6" />
          </Button>
        </div>
      </div>

      {/* Camera / Preview */}
      <div className="h-full flex items-center justify-center">
        {!cameraActive && !capturedImage && (
          <div className="text-center p-8">
            <Camera className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Measure Pin Distance</h3>
            <p className="text-zinc-400 text-sm mb-6 max-w-xs mx-auto">
              Point your camera at the ball on the green. We&apos;ll use the ball size 
              ({GOLF_BALL_DIAMETER_INCHES}&quot; diameter) as reference to calculate distance.
            </p>
            <Button onClick={startCamera} className="bg-[#007AFF] hover:bg-[#38BDF8]">
              <Camera className="w-4 h-4 mr-2" />
              Open Camera
            </Button>
            {error && (
              <p className="text-red-400 text-sm mt-4">{error}</p>
            )}
          </div>
        )}

        {cameraActive && (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
            {/* Crosshair Overlay */}
            <div className="absolute inset-0 pointer-events-none">
              {/* Scan lines effect */}
              <div 
                className="absolute inset-0 opacity-20"
                style={{
                  background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,122,255,0.1) 2px, rgba(0,122,255,0.1) 4px)'
                }}
              />
              {/* Crosshair */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="relative">
                  <Target className="w-24 h-24 text-[#007AFF] animate-pulse" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 border-2 border-yellow-400 rounded-full" />
                </div>
              </div>
              {/* Instructions */}
              <div className="absolute bottom-32 left-0 right-0 text-center">
                <p className="text-white text-sm bg-black/50 inline-block px-4 py-2 rounded-full">
                  Center the ball in the crosshair
                </p>
              </div>
            </div>
          </>
        )}

        {capturedImage && !result && (
          <div className="relative w-full h-full">
            <img 
              src={capturedImage} 
              alt="Captured" 
              className="w-full h-full object-cover"
            />
            {analyzing && (
              <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                <div className="text-center">
                  <Loader2 className="w-12 h-12 text-[#007AFF] animate-spin mx-auto mb-4" />
                  <p className="text-white font-semibold">Analyzing...</p>
                  <p className="text-zinc-400 text-sm">Calculating distance from ball to pin</p>
                </div>
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="text-center p-8">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-green-400" />
            </div>
            <p className="text-zinc-400 text-sm mb-2">Estimated Distance</p>
            <p className="text-5xl font-black text-white mb-2">
              {result.distance_display}
            </p>
            <p className="text-zinc-500 text-xs mb-6">
              Confidence: {Math.round(result.confidence * 100)}%
            </p>
            <Card className="bg-yellow-500/10 border-yellow-500/30 max-w-xs mx-auto mb-6">
              <CardContent className="p-3 text-center">
                <p className="text-yellow-300 text-xs">
                  ⚠️ AI MOCKED: In production, this would use computer vision to detect 
                  the ball and calculate actual distance.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Bottom Controls */}
      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black to-transparent">
        <canvas ref={canvasRef} className="hidden" />
        
        {cameraActive && (
          <div className="flex justify-center">
            <button
              onClick={captureImage}
              className="w-20 h-20 rounded-full bg-white border-4 border-[#007AFF] flex items-center justify-center"
              data-testid="capture-btn"
            >
              <div className="w-16 h-16 rounded-full bg-[#007AFF]" />
            </button>
          </div>
        )}

        {capturedImage && !result && !analyzing && (
          <div className="flex gap-4 justify-center">
            <Button
              variant="outline"
              onClick={reset}
              className="border-white/20"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Retake
            </Button>
            <Button
              onClick={analyzeImage}
              className="bg-[#007AFF] hover:bg-[#38BDF8]"
              data-testid="analyze-btn"
            >
              <Target className="w-4 h-4 mr-2" />
              Analyze Distance
            </Button>
          </div>
        )}

        {result && (
          <div className="flex gap-4 justify-center">
            <Button
              variant="outline"
              onClick={reset}
              className="border-white/20"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Retake
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-green-600 hover:bg-green-500"
              data-testid="submit-ctp-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Submit Entry
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
